<template>
  <div>
    <b-dropdown
      class="float-right"
      variant="link"
      toggle-class="text-decoration-none"
      no-caret
      right
    >
      <template #button-content>
        <span class="svg-icon svg-icon-lg">
          <inline-svg src="media/svg/icons/General/Menu.svg" />
        </span>
      </template>
      <b-dropdown-item @click="CSV()">CSV</b-dropdown-item>
      <b-dropdown-item @click="saveImg()">PNG</b-dropdown-item>
    </b-dropdown>
    <br /><br /><br />
    <v-chart class="chart" :option="option" />
  </div>
</template>

<script>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";
import { Config } from "@/core/config/variable.config";
import Swal from "sweetalert2";
import { DASHBOARD_RECEIPT_REWARD_POINT_EXPORT_CAMPAIGN } from "@/core/services/store/campaign.module";

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

export default {
  name: "ReceiptRewardPointDonutChart",
  components: {
    VChart
  },
  provide: {
    [THEME_KEY]: "light"
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    isProPlan: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: [String, Number],
      default: null
    },
    filterFrom: {
      type: Date,
      default: null
    },
    filterTo: {
      type: Date,
      default: null
    }
  },
  data() {
    return {
      option: {
        title: {
          text: `${this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT"
          )}`,
          left: "center"
        },
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            return `${params.name} : ${params.value
              .toFixed(2)
              .replace(/\d(?=(\d{3})+\.)/g, "$&,")} 
              (${params.percent}%)`;
          }
        },
        avoidLabelOverlap: false,
        legend: {
          bottom: "bottom",
          type: "scroll",
          data: []
        },
        series: [
          {
            name: `${this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT"
            )}`,
            type: "pie",
            radius: ["40%", "70%"],
            avoidLabelOverlap: false,
            label: {
              show: true,
              position: "center",
              fontSize: "20",
              formatter: () => {
                return `0`;
              }
            },
            labelLine: {
              show: false
            },
            data: [],
            color: Config.GRAPH_COLOR
          }
        ],
        backgroundColor: "white"
      }
    };
  },
  mounted() {
    if (this.data) {
      let seriesData = [];
      let sum = 0;
      for (let index = 0; index < this.data.series.length; index++) {
        const element = this.data.series[index];
        seriesData.push({
          name: this.data.labels[index],
          value: element
        });
        sum += element;
      }
      if (sum > 0) {
        this.option.legend.data = this.data.labels;
        this.option.series = [
          {
            name: `${this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT"
            )}`,
            type: "pie",
            radius: ["0", "0"],
            avoidLabelOverlap: false,
            label: {
              show: true,
              position: "center",
              fontSize: "20",
              formatter: () => {
                return `${sum.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
              }
            },
            labelLine: {
              show: false
            },
            data: seriesData,
            color: Config.GRAPH_COLOR
          },
          {
            name: `${this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT"
            )}`,
            type: "pie",
            radius: ["40%", "70%"],
            avoidLabelOverlap: false,
            label: {
              show: true,
              position: "inner",
              fontSize: "16",
              formatter: (params) => {
                return `${params.percent}%`;
              }
            },
            labelLine: {
              show: false
            },
            data: seriesData,
            color: Config.GRAPH_COLOR
          }
        ];
      }
    }
  },
  methods: {
    csvEscapeCell(val) {
      const s = String(val == null ? "" : val);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    },
    apiCatchMessage(error) {
      if (error && error.message) return error.message;
      if (error && error.response && error.response.data) {
        const d = error.response.data;
        if (typeof d === "string") return d;
        if (d.message) return d.message;
        if (d.error) return d.error;
      }
      return error != null ? String(error) : "Unknown error";
    },
    async CSV() {
      if (this.isProPlan && this.campaignId != null && this.campaignId !== "") {
        await this.exportCsvPro();
        return;
      }
      if (!this.data || !this.data.labels || !this.data.series) {
        return;
      }
      let headers = [
        `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT"
        )}`,
        "จำนวน"
      ];

      // prepare CSV data
      let csvData = new Array();
      csvData.push(headers);
      for (let index = 0; index < this.data.labels.length; index++) {
        const element = this.data.labels[index];
        csvData.push(new Array(element, this.data.series[index]));
      }

      let lineArray = [];
      csvData.forEach(function (infoArray, index) {
        let line = infoArray.join(",");
        lineArray.push(index == 0 ? "\uFEFF" + line : line);
      });
      let csvContent = lineArray.join("\n");

      // download stuff
      let blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      let link = document.createElement("a");

      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        link.setAttribute("href", window.URL.createObjectURL(blob));
        link.setAttribute(
          "download",
          `${this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT"
          )}.csv`
        );
        link.setAttribute("hidden", true);
      } else {
        // it needs to implement server side export
        link.setAttribute("href", "#");
      }
      link.click();
    },
    async exportCsvPro() {
      try {
        const { data } = await this.$store.dispatch(
          DASHBOARD_RECEIPT_REWARD_POINT_EXPORT_CAMPAIGN,
          {
            id: this.campaignId,
            params: {
              start_date: this.filterFrom,
              end_date: this.filterTo
            }
          }
        );
        if (data.status === "success" && data.data && data.data.rows) {
          const rows = data.data.rows;
          const headers = [
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT_EXPORT_COL_NAME"
            ),
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT_EXPORT_TOTAL_VALUE"
            ),
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT_EXPORT_NAMES"
            )
          ];
          const csvData = [headers];
          for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            csvData.push([
              this.csvEscapeCell(r.reward_name),
              this.csvEscapeCell(r.total_value),
              this.csvEscapeCell(r.names)
            ]);
          }
          const lineArray = [];
          csvData.forEach(function (infoArray, index) {
            const line = infoArray.join(",");
            lineArray.push(index === 0 ? "\uFEFF" + line : line);
          });
          const csvContent = lineArray.join("\n");
          const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;"
          });
          const link = document.createElement("a");
          if (link.download !== undefined) {
            link.setAttribute("href", window.URL.createObjectURL(blob));
            link.setAttribute(
              "download",
              `${this.$t(
                "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT"
              )}_pro_detail.csv`
            );
            link.setAttribute("hidden", true);
          } else {
            link.setAttribute("href", "#");
          }
          link.click();
        } else if (data.status === "error" && data.error) {
          Swal.fire({
            icon: "error",
            text: `${this.$t("RESPONSE_MESSAGE." + data.error)}`,
            showConfirmButton: true,
            confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: this.apiCatchMessage(error),
          showConfirmButton: true,
          confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
        });
      }
    },
    saveImg() {
      let canvas = document.getElementsByTagName("canvas");
      if (canvas && canvas.length > 0) {
        // create label
        let tempA = document.createElement("a");
        // Set download name
        tempA.download = `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.RECEIPT_REWARD_POINT"
        )}.png`;
        // Set address and file type
        tempA.href = canvas[5].toDataURL("image/png");
        document.body.appendChild(tempA);
        // Trigger download event
        tempA.click();
        // Remove Tag
        tempA.remove();
      }
    }
  }
};
</script>

<style scoped>
.chart {
  height: 500px;
}
</style>

<style>
body {
  margin: 0;
}
</style>
